import ApiServiceBase from "@/services/api/apiServiceBase";
import {alertController} from "@ionic/vue";
import router from "@/router";
import {Preferences} from "@capacitor/preferences";
import {UserService} from "@/services/api/user/userService";
import {PreferenceKey} from "@/definitions/PreferenceKey";

export default class AuthService extends ApiServiceBase {

    public async login(email: string, password: string) {
        const data = await this.http?.post('/auth/login', {
            email,
            password,
            deviceType: 'web',
        }, false);

        if (data?.token) {
            await Preferences.set({key: PreferenceKey.ApiToken, value: data.token});
        }

        // Always refresh via GET /profile + preferences so onboarding gate has prefs
        const profile = await new UserService().get();
        return {token: data?.token, profile};
    }

    public async register(email: string, password: string) {
        return this.http?.post('/auth/register', {
            email,
            password,
            password_confirmation: password,
        }, false);
    }

    public async verifyEmail(email: string, token: string) {
        const data = await this.http?.post('/auth/verify_email', {email, token}, false);

        if (data?.token) {
            await Preferences.set({key: PreferenceKey.ApiToken, value: data.token});
        }

        const profile = await new UserService().get();
        return {token: data?.token, profile, message: data?.message};
    }

    public async resendVerification(email: string) {
        return this.http?.post('/auth/resend_verification', {email}, false);
    }

    public async forgotPassword(email: string) {
        return this.http?.post('/auth/forgot_password', {email}, false);
    }

    public async changePassword(password: string, password_confirmation: string) {
        return this.http?.post('/auth/change_password', {password, password_confirmation});
    }

    public async logout(callback?: () => void) {
        const alert = await alertController.create({
            header: 'Logout',
            message: 'Are you sure you want to logout?',
            buttons: [
                {text: 'Cancel', role: 'cancel'},
                {
                    text: 'Logout',
                    role: 'destructive',
                    handler: async () => {
                        await alertController.dismiss(true);
                        await Preferences.clear();
                        if (callback) callback();
                    },
                },
            ],
        });

        await alert.present();
        await alert.onWillDismiss();
    }

    public async deleteAccount(callback?: () => void) {
        const alert = await alertController.create({
            header: 'Delete Account',
            message: 'Are you sure you want to delete your account?',
            buttons: [
                {text: 'Cancel', role: 'cancel'},
                {
                    text: 'Yes',
                    role: 'destructive',
                    handler: async () => {
                        await this.http?.delete('/profile', {});
                        await Preferences.clear();
                        await router.replace('/');
                        if (callback) callback();
                    },
                },
            ],
        });

        await alert.present();
    }
}
